import { useState, useEffect } from 'react';
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';
import Breadcrumb from 'react-bootstrap/Breadcrumb';
import ListGroup from 'react-bootstrap/ListGroup';
import Form from 'react-bootstrap/Form';
import Dropdown from 'react-bootstrap/Dropdown';
import useApp from './../AppContext';
import { t } from './../i18n';
import { callApi, ErrorAlert } from './utils';

import './FieldSelector.css';

function FieldSelector(props) {
    const app = useApp();
    const [isOpen, setIsOpen] = useState(false);
    const [serverUrl] = useState(app.serverUrl);
    const [objectFields, setObjectFields] = useState(null);
    const [navQueue, setNavQueue] = useState([]);
    const [navPath, setNavPath] = useState(null);
    const [selectedFields, setSelectedFields] = useState([]);
    const [objectName, setObjectName] = useState(props.objectName);
    const language = localStorage.getItem('preferredLanguage') || app.sessionToken?.Language

    const [error, setError] = useState(null);

    useEffect(() => {
        async function getLayout() {
            try {
                if (!objectName || !serverUrl) return;
                setError(null);
                let objectFields = await callApi('GET', `${serverUrl}/api/layouts/objectFields/${objectName}`, props.userToken);
                if (navPath) {
                    objectFields.forEach(f => f.columnName = navPath + "." + f.columnName)
                }
                setObjectFields(objectFields);
            } catch (err) {
                setError(err.message);
            }
        };
        getLayout();
    }, [navPath, objectName, props.userToken, serverUrl]);

    function onOpen() {
        setIsOpen(true);
        let navQueue = [{ objectName: props.objectName, objectCaption: props.objectCaption, navProp: "" }];
        setNavQueue(navQueue);
        setNavPath("");
        setObjectName(props.objectName);

        let selectedFields = props.layoutColumns.map(c => ({
            columnName: c.columnName
        }));
        setSelectedFields(selectedFields);
    }

    function updateNavPath(navQueue) {
        let navPath = navQueue.map(p => p.navProp).join('.');
        if (navPath.startsWith('.')) {
            if (navPath.length === 1) navPath = '';
            else navPath = navPath.substr(1);
        }
        setNavPath(navPath);
    }

    function goBack(i) {
        if (i === navQueue.length - 1) return;
        let navQueueTmp = navQueue;
        navQueueTmp.splice(i + 1);
        setNavQueue(navQueueTmp);
        setObjectName(navQueue[i].objectName);
        updateNavPath(navQueueTmp);
    }

    function createBreadCump() {
        let breadcrumbItems = navQueue.map((p, i) =>
            <Breadcrumb.Item key={`breadcrumb-${i}`} onClick={() => goBack(i)} style={{ paddingTop: "10px" }}>{p.objectCaption}</Breadcrumb.Item>
        );
        return (
            <Breadcrumb>
                {breadcrumbItems}
            </Breadcrumb>
        );
    }

    function fieldCheckClicked(f) {
        let selectedFieldsTmp = [...selectedFields];
        let selectedFieldIndex = selectedFieldsTmp.findIndex(field => field.columnName === f.columnName);
        if (selectedFieldIndex !== -1) selectedFieldsTmp.splice(selectedFieldIndex, 1);
        else {
            selectedFieldsTmp.push(f);
        }
        setSelectedFields(selectedFieldsTmp);
    }

    function isFieldSelected(f) {
        return (selectedFields.find(field => field.columnName === f.columnName) != null);
    }

    function objectSelected(f) {
        let navQueueTmp = [...navQueue, { objectName: f.refObject, objectCaption: f.caption, navProp: f.navProp }];
        setNavQueue(navQueueTmp);
        setObjectName(f.refObject);

        updateNavPath(navQueueTmp);
    }

    function createFieldList() {
        if (!objectFields) return null;
        let fieldItems = objectFields.map((f, i) =>
            <ListGroup.Item key={"li" + i}>
                <Form key={"frm" + i}>
                    <div className="col-9 d-flex align-items-start">
                        <Form.Check
                            type="checkbox"
                            className="custom-checkbox field-checkbox"
                            style={{ marginRight: '10px', marginTop: '0px', marginLeft: '0px', marginBottom: '0px', alignItems: 'start' }}
                            onChange={() => fieldCheckClicked(f)}
                            checked={isFieldSelected(f)}
                        />
                        {(f.refObject === null) ?
                            <Form.Label className="mb-0">{f.caption}</Form.Label> :
                            <Form.Label
                                style={{ cursor: 'pointer', textDecoration: 'none' }}
                                as='a'
                                onClick={() => objectSelected(f)}
                            >{f.caption}</Form.Label>
                        }
                    </div>
                </Form>
            </ListGroup.Item>);

        return (
            <ListGroup className="field-list-group">
                {fieldItems}
            </ListGroup>
        );
    }

    async function saveLayoutColumns(cols) {
        try {
            setError(null);
            let url = `${serverUrl}/api/layouts/list/${props.layoutId}`;
            if (props.instanceName) url += `?instanceName=${props.instanceName}`;
            await callApi('POST', url, props.userToken, { columnNames: cols });
            if (props.onSelectedFieldsChanged) props.onSelectedFieldsChanged();
            setIsOpen(false);
        } catch (err) {
            setError(err.message);
        }
    }
    async function reset() {
        saveLayoutColumns([]);
    }

    async function apply() {
        saveLayoutColumns(selectedFields.map(f => f.columnName));
    }

    function createModal() {
        if (!isOpen) return null;

        return (
            <Modal
                show={isOpen}
                onHide={() => setIsOpen(false)}
                backdrop="static"
                size='lg'
                keyboard={false}
            >
                <Modal.Header closeButton>
                    <Modal.Title>  {t('toolkit.fields', language)}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <ErrorAlert message={error} setMessage={setError} language={language}/>
                    {createBreadCump()}
                    {createFieldList()}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={reset} style={{ background: "transparent", border: "2px solid var(--color-main-background)", color: 'var(--color-main-background)', fontWeight: 500, borderRadius: "22px" }}>  {t('common.resetButton', language)}</Button>
                    <Button variant="primary" onClick={apply} style={{ background: "var(--color-main-background)", fontWeight: 500, border: "1px solid var(--color-main-background)", borderRadius: "22px" }}>  {t('common.apply', language)}</Button>
                </Modal.Footer>
            </Modal>);
    }

    return (
        <>
            <Dropdown.Item className="btn-sm" onClick={onOpen}>
                {t('toolkit.fieldChooser', language)}{/* <CardListIcon/>  */}
            </Dropdown.Item>
            {createModal()}
        </>
    );
}

export default FieldSelector;